import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import API from "../../config";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isFetching, setIsFetching] = useState(false); 
    const { t } = useTranslation();

    const isAuthenticated = useCallback(() => {
        const token = localStorage.getItem("token");
        return Boolean(token);
    }, []);

    const fetchNotifications = useCallback(async (isInitialLoad = false) => {
        if (!isAuthenticated()) {
            return;
        }
    
        try {
            setIsFetching(true);
    
            const response = await axios.get(`${API}/notifications?limit=20`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
    
            const fetchedNotifications = response.data.map((notification) => ({
                ...notification,
                related_user_link: notification.related_user_link || null,
                related_link: notification.related_link || null,
                preview_image_url: notification.preview_image_url || null,
            }));
    
            if (isInitialLoad) {
                setNotifications(fetchedNotifications);
                const unread = fetchedNotifications.filter((n) => !n.is_read).length;
                setUnreadCount(unread);
            } else {
                setNotifications((prevNotifications) => {
                    const newNotifications = fetchedNotifications.filter(
                        (newNotif) => !prevNotifications.some((oldNotif) => oldNotif.id === newNotif.id)
                    );
                    return [...newNotifications, ...prevNotifications];
                });
                const unread = fetchedNotifications.filter((n) => !n.is_read).length;
                setUnreadCount(unread);
            }
        } catch (error) {
            // toast.error("Error loading notifications");
        } finally {
            setIsFetching(false);
        }
    }, [isAuthenticated]); 


    const markNotificationAsRead = useCallback(async (notificationId) => {
        if (!isAuthenticated()) {
            return;
        }

        try {
            await axios.post(`${API}/notifications/mark-read/${notificationId}`, null, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            setNotifications((prevNotifications) => {
                const updatedNotifications = prevNotifications.map((notification) =>
                    notification.id === notificationId
                        ? { ...notification, is_read: true }
                        : notification
                );
                return updatedNotifications;
            });

            toast.success(t("Notification marked as read successfully."));
            setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
        } catch (error) {
            toast.error(t("Failed to mark notification as read"));
        }
    }, [t, isAuthenticated]);

    const markAllAsRead = useCallback(async () => {
        if (!isAuthenticated()) {
            console.warn("Пользователь не авторизован. Запрос отклонен.");
            return;
        }

        try {
            await axios.post(`${API}/notifications/mark-all-read`, null, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            setNotifications((prev) => {
                const updatedNotifications = prev.map((notification) => ({
                    ...notification,
                    is_read: true,
                }));
                return updatedNotifications;
            });

            toast.success(t("All notifications marked as read."));
            setUnreadCount(0);
        } catch (error) {
            toast.error(t("Failed to mark all notifications as read"));
        }
    }, [t, isAuthenticated]);

    useEffect(() => {
        fetchNotifications(true); 
    }, [fetchNotifications]);

    useEffect(() => {
        if (!isAuthenticated()) return;

        const intervalId = setInterval(() => {
            if (!isFetching) {
                fetchNotifications(false); 
            }
        }, 90000); 

        return () => clearInterval(intervalId);
    }, [fetchNotifications, isFetching, isAuthenticated]);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                fetchNotifications,
                markNotificationAsRead,
                markAllAsRead,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotificationContext = () => useContext(NotificationContext);